import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useSearchParams } from "@repo/core/hooks/useSearchParams";
import { ROUTES } from "@repo/core/routes";
import { useCreditProposal } from "@repo/core/screens/ProposalEffectiveFlows/Credit/hooks/useCreditProposal";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { apiInstance } from "@repo/core/services/instances/api";
import { isUnicoDocumentType } from "@repo/core/enums/proposals";
import type {
  PreProposalData,
  RequiredDocuments,
  SetProposalDataOptions
} from "../../../store/preProposalStore";
import {
  getUploadedProposalDocumentTypes,
  mandatoryDocumentsIncludeBiometry,
  mergeRequiredDocumentsWithUploadedStatus
} from "../utils";
import { STATUS_MESSAGES } from "../constants";
import {
  shouldNavigateToFinancialService,
  navigateToReanalysisCreditFlowIfApplicable,
  isReanalysisProposal
} from "../services/navigation.service";
import {
  ensureRequiredBiometryDocument,
  filterBiometryUploadedDocumentTypes
} from "../../../services/proposalEditBiometry.service";

const proposalsResource = new ProposalsResource(apiInstance);

interface UseRequiredDocumentsParams {
  proposalData: PreProposalData | null;
  setRequiredDocuments: (documents: RequiredDocuments[]) => void;
  setStatusMessage: (message: string) => void;
  setProposalData: (
    proposalData: Partial<PreProposalData>,
    options?: SetProposalDataOptions
  ) => void;
}

export const useRequiredDocuments = ({
  proposalData,
  setRequiredDocuments,
  setStatusMessage,
  setProposalData
}: UseRequiredDocumentsParams) => {
  const { push, replace, resetTo } = useNavigation();
  const { clearCreditProposal } = useCreditProposal();
  const searchParams = useSearchParams();
  const processedDocumentsRef = useRef<string | null>(null);

  // If verificationId exists in URL, it means user is returning from Unico process
  // Don't execute the query again in this case
  const hasVerificationId = !!searchParams?.verificationId;

  const {
    data: requiredDocumentsResponse,
    isFetched: isFetchedRequiredDocuments
  } = useQuery({
    queryKey: ["requiredDocuments", proposalData?.strProduto],
    queryFn: () =>
      proposalsResource.validateRequiredDocuments(
        proposalData?.strProduto || ""
      ),
    select: (data) => data.data,
    enabled: !hasVerificationId
  });

  const {
    data: proposalDocumentsResponse,
    isFetched: isFetchedProposalDocuments
  } = useQuery({
    queryKey: ["proposalDocumentsList", proposalData?.lngProposta],
    queryFn: () =>
      proposalsResource.getDocumentsList(
        String(proposalData?.lngProposta || "")
      ),
    select: (data) => data.data.documentos,
    enabled: !hasVerificationId && !!proposalData?.lngProposta
  });

  const shouldForceNewBiometry = proposalData?.shouldForceNewBiometry === true;
  const requiredDocumentsResponseIncludesBiometry =
    mandatoryDocumentsIncludeBiometry(requiredDocumentsResponse);
  const shouldApplyForcedBiometry =
    shouldForceNewBiometry && requiredDocumentsResponseIncludesBiometry;
  const shouldWaitForProposalDocuments =
    !!proposalData?.lngProposta &&
    !isFetchedProposalDocuments &&
    !!requiredDocumentsResponse &&
    (shouldApplyForcedBiometry || requiredDocumentsResponseIncludesBiometry);

  const isRequiredDocumentsContextReady =
    isFetchedRequiredDocuments && !shouldWaitForProposalDocuments;

  const effectiveRequiredDocuments = useMemo(() => {
    if (!requiredDocumentsResponse || !isFetchedRequiredDocuments) {
      return undefined;
    }

    if (shouldWaitForProposalDocuments) {
      return undefined;
    }

    const uploadedProposalDocumentTypes = getUploadedProposalDocumentTypes(
      proposalDocumentsResponse
    );
    const effectiveUploadedProposalDocumentTypes = shouldApplyForcedBiometry
      ? filterBiometryUploadedDocumentTypes(uploadedProposalDocumentTypes)
      : uploadedProposalDocumentTypes;
    const enrichedRequiredDocuments = mergeRequiredDocumentsWithUploadedStatus(
      requiredDocumentsResponse,
      effectiveUploadedProposalDocumentTypes
    );

    return shouldApplyForcedBiometry
      ? ensureRequiredBiometryDocument(enrichedRequiredDocuments)
      : enrichedRequiredDocuments;
  }, [
    isFetchedRequiredDocuments,
    isFetchedProposalDocuments,
    proposalDocumentsResponse,
    requiredDocumentsResponse,
    shouldApplyForcedBiometry,
    shouldWaitForProposalDocuments
  ]);

  useEffect(() => {
    // Skip processing if user is returning from Unico (has verificationId)
    if (hasVerificationId) {
      return;
    }

    if (!effectiveRequiredDocuments) {
      return;
    }

    const uploadedProposalDocumentTypes = getUploadedProposalDocumentTypes(
      proposalDocumentsResponse
    );
    const effectiveUploadedProposalDocumentTypes = shouldApplyForcedBiometry
      ? filterBiometryUploadedDocumentTypes(uploadedProposalDocumentTypes)
      : uploadedProposalDocumentTypes;
    const documentsKey = JSON.stringify({
      lngProposta: proposalData?.lngProposta,
      isReanalysis: isReanalysisProposal(proposalData),
      shouldForceNewBiometry: shouldApplyForcedBiometry,
      docs: effectiveRequiredDocuments.map((doc) => ({
        tipo: doc.strTipoDocumento,
        descricao: doc.strDescricao,
        isUploaded: doc.isUploaded === true
      }))
    });

    if (processedDocumentsRef.current === documentsKey) {
      return;
    }

    processedDocumentsRef.current = documentsKey;
    setRequiredDocuments(effectiveRequiredDocuments);
    setProposalData(
      {
        uploadedProposalDocumentTypes: effectiveUploadedProposalDocumentTypes,
        restartRequired: false,
        shouldForceManualCapture: false,
        shouldForceNewBiometry: shouldApplyForcedBiometry
      },
      { skipAlterarSync: true }
    );

    //TODO REMOVE WHEN preProposalsSelectFinancialService THE FLOW RETURNS
    if (shouldNavigateToFinancialService(effectiveRequiredDocuments)) {
      if (
        navigateToReanalysisCreditFlowIfApplicable(
          proposalData,
          resetTo,
          clearCreditProposal
        )
      ) {
        return;
      }
      // push(ROUTES.preProposalsSelectFinancialService);
      replace(ROUTES.preProposalsVerifyInformations);
      return;
    }

    const pendingRequiredDocuments = effectiveRequiredDocuments.filter(
      (document) => document.isUploaded !== true
    );
    const mandatoryIncludesBiometry = mandatoryDocumentsIncludeBiometry(
      effectiveRequiredDocuments
    );

    const hasPendingUnicoDocuments =
      mandatoryIncludesBiometry &&
      pendingRequiredDocuments.some((document) =>
        isUnicoDocumentType(String(document.strTipoDocumento).trim())
      );

    const hasPendingManualDocuments = pendingRequiredDocuments.some(
      (document) => {
        const type = String(document.strTipoDocumento).trim();
        if (!isUnicoDocumentType(type)) {
          return true;
        }
        return !mandatoryIncludesBiometry;
      }
    );

    if (pendingRequiredDocuments.length === 0) {
      if (
        navigateToReanalysisCreditFlowIfApplicable(
          proposalData,
          resetTo,
          clearCreditProposal
        )
      ) {
        return;
      }
      replace(ROUTES.preProposalsVerifyInformations);
      return;
    }

    if (!hasPendingUnicoDocuments && hasPendingManualDocuments) {
      const forceManualCaptureForDocumentTypes =
        !mandatoryIncludesBiometry &&
        pendingRequiredDocuments.some((document) =>
          isUnicoDocumentType(String(document.strTipoDocumento).trim())
        );

      if (forceManualCaptureForDocumentTypes) {
        setProposalData(
          { shouldForceManualCapture: true },
          { skipAlterarSync: true }
        );
      }

      setStatusMessage(STATUS_MESSAGES.REDIRECT_TO_MANUAL_CAPTURE);
      push(ROUTES.preProposalsSelectDocuments, {
        ...(forceManualCaptureForDocumentTypes && { forceManualCapture: true })
      });
      return;
    }

    if (hasPendingUnicoDocuments) {
      setStatusMessage(STATUS_MESSAGES.STARTING_PROCESS);
    }
  }, [
    hasVerificationId,
    effectiveRequiredDocuments,
    proposalDocumentsResponse,
    setRequiredDocuments,
    setProposalData,
    replace,
    resetTo,
    push,
    proposalData,
    clearCreditProposal,
    setStatusMessage,
    shouldApplyForcedBiometry
  ]);

  return {
    requiredDocuments: effectiveRequiredDocuments,
    isFetchedRequiredDocuments,
    isRequiredDocumentsContextReady
  };
};
